import { useEffect, useState } from "react";

import { fetchRecommendations, toggleCollectionFavorite } from "./api";
import { navigate } from "./navigation";
import { publishFavoriteChange, subscribeFavoriteChanges } from "./favorites";
import { subscribeScoreInvalidations } from "./score";
import type { RecommendationItem } from "./types";

export function RecommendationsPage() {
  const [items, setItems] = useState<RecommendationItem[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeScoreInvalidations(() => {
    setPage(1);
    setRevision((value) => value + 1);
  }), []);

  useEffect(() => {
    let active = true;
    void fetchRecommendations()
      .then((payload) => {
        if (!active) return;
        setItems(payload.recommendations);
        setHasNext(payload.pagination.has_next);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Recommandations indisponibles.");
      });
    return () => { active = false; };
  }, [revision]);

  useEffect(
    () => subscribeFavoriteChanges((change) => {
      setItems((current) => current?.map((candidate) => candidate.collection.id === change.collectionId ? { ...candidate, collection: { ...candidate.collection, is_favorite: change.isFavorite } } : candidate) ?? null);
    }),
    [],
  );

  const loadMore = async () => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const payload = await fetchRecommendations(nextPage);
      setItems((current) => [...(current ?? []), ...payload.recommendations]);
      setPage(nextPage);
      setHasNext(payload.pagination.has_next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Page suivante indisponible.");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleFavorite = async (item: RecommendationItem) => {
    if (savingId !== null) return;
    setSavingId(item.collection.id);
    setError(null);
    try {
      const result = await toggleCollectionFavorite(item.collection.id, !item.collection.is_favorite);
      setItems((current) => current?.map((candidate) => candidate.collection.id === item.collection.id ? { ...candidate, collection: { ...candidate.collection, is_favorite: result.is_favorite } } : candidate) ?? null);
      publishFavoriteChange({ collectionId: item.collection.id, isFavorite: result.is_favorite });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Favori non modifié.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section className="collection-page recommendations-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>← Retour au tableau de bord</button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">Sélection personnalisée</p>
          <h1>Mes recommandations</h1>
          <p>Des séries choisies selon votre classe, votre niveau et les cartes à revoir.</p>
        </div>
        <span className="section-chip">Pour vous</span>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {items === null && !error ? <div className="collection-loading">Préparation de vos recommandations…</div> : items?.length ? (
        <>
          <div className="recommendations-grid">
            {items.map((item) => (
              <article className="recommendation-card" key={item.collection.id}>
                <div className="recommendation-reason"><span aria-hidden="true">✦</span><strong>{item.reason}</strong></div>
                <div className="collection-tile-top"><span className="subject-color" style={{ backgroundColor: item.collection.subject.color }} /><span>{item.collection.subject.name}</span>{item.recently_played && <small>Jouée récemment</small>}</div>
                <h2>{item.collection.title}</h2>
                <p>{item.collection.description ?? "Une série sélectionnée pour votre progression."}</p>
                <div className="collection-meta"><span>{item.collection.cards_count} cartes</span><span>{item.game_label} · {item.difficulty_label}</span></div>
                <div className="recommendation-actions">
                  <button className="button button-primary" type="button" onClick={() => navigate(item.play_path)}>Jouer à {item.game_label}</button>
                  <button className="button" type="button" onClick={() => navigate(item.collection.path)}>Voir la série</button>
                  <button className="favorite-button" type="button" disabled={savingId !== null} aria-pressed={item.collection.is_favorite} aria-label={item.collection.is_favorite ? "Retirer des favoris" : "Ajouter aux favoris"} onClick={() => void toggleFavorite(item)}>{item.collection.is_favorite ? "★" : "☆"}</button>
                </div>
              </article>
            ))}
          </div>
          {hasNext && <button className="button" type="button" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "Chargement…" : "Afficher plus de recommandations"}</button>}
        </>
      ) : <div className="empty-state collection-empty"><strong>Aucune recommandation pour le moment</strong><p>Explorez les ressources officielles ou demandez une proposition à votre enseignant.</p><button className="button button-primary" type="button" onClick={() => navigate("/ressources-officielles")}>Explorer les ressources</button></div>}
    </section>
  );
}
